import { useRef, useCallback, useState } from 'react'

/**
 * Generic undo/redo history hook.
 * Stores up to `maxSize` snapshots and provides navigation.
 */
export interface UseHistoryReturn<T> {
  /** Push a new state snapshot. Truncates any forward (redo) history. */
  push: (state: T) => void
  /** Go back one step. Returns the restored state, or undefined if at start. */
  undo: () => T | undefined
  /** Go forward one step. Returns the restored state, or undefined if at end. */
  redo: () => T | undefined
  /** Whether undo is available */
  canUndo: boolean
  /** Whether redo is available */
  canRedo: boolean
  /** Reset the entire history with a new base state */
  reset: (state: T) => void
}

export function useHistory<T>(maxSize = 16): UseHistoryReturn<T> {
  const historyRef = useRef<T[]>([])
  const pointerRef = useRef(-1)
  // Revision counter forces React re-renders when history changes
  const [, setRevision] = useState(0)
  const bump = () => setRevision((r) => r + 1)

  const push = useCallback(
    (state: T) => {
      const history = historyRef.current
      const pointer = pointerRef.current
      const serialized = JSON.stringify(state)

      // Skip if identical to current state
      if (pointer >= 0 && pointer < history.length) {
        if (JSON.stringify(history[pointer]) === serialized) return
      }

      // Truncate any forward history (redo states)
      const newHistory = history.slice(0, pointer + 1)
      newHistory.push(JSON.parse(serialized) as T)

      // Enforce max size — drop oldest entries
      if (newHistory.length > maxSize) {
        newHistory.splice(0, newHistory.length - maxSize)
      }

      historyRef.current = newHistory
      pointerRef.current = newHistory.length - 1
      bump()
    },
    [maxSize],
  )

  const undo = useCallback((): T | undefined => {
    if (pointerRef.current <= 0) return undefined
    pointerRef.current -= 1
    bump()
    return JSON.parse(JSON.stringify(historyRef.current[pointerRef.current])) as T
  }, [])

  const redo = useCallback((): T | undefined => {
    if (pointerRef.current >= historyRef.current.length - 1) return undefined
    pointerRef.current += 1
    bump()
    return JSON.parse(JSON.stringify(historyRef.current[pointerRef.current])) as T
  }, [])

  const reset = useCallback((state: T) => {
    const copy = JSON.parse(JSON.stringify(state)) as T
    historyRef.current = [copy]
    pointerRef.current = 0
    bump()
  }, [])

  const canUndo = pointerRef.current > 0
  const canRedo = pointerRef.current < historyRef.current.length - 1

  return { push, undo, redo, canUndo, canRedo, reset }
}
